import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import FileUpload, { FileChip } from './FileUpload';
import { Folder, Filter } from 'lucide-react';

const CONTEXT_LABELS = {
  homework: 'Homework',
  'coding-project': 'Coding Projects',
  'profile-pic': 'Profile Pictures',
  'intro-video': 'Intro Videos',
  'class-recording': 'Class Recordings',
  receipt: 'Payment Receipts',
  thumbnail: 'Thumbnails',
  'certificate-template': 'Certificate Templates',
  csv: 'CSVs',
  misc: 'Other',
};

/**
 * My Files — self-service uploads manager. Any authenticated user can
 * upload / browse / delete their own files. Groups by `context` chip filter.
 */
export default function MyFilesPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');   // '' = all contexts

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/uploads/mine/list', { params: { limit: 200 } });
      setFiles(res.data.files || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const deleteFile = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/uploads/${id}`);
      toast.success('Deleted');
      setFiles(files.filter(f => f.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const contextsInUse = Array.from(new Set(files.map(f => f.context))).sort();
  const visible = filter ? files.filter(f => f.context === filter) : files;

  return (
    <div className="p-6 space-y-5" data-testid="my-files-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Folder className="w-6 h-6 text-indigo-600" /> My Files
          </h1>
          <p className="text-xs text-slate-500 mt-1">Upload homework, projects, receipts, or anything else — max 25 MB per file (100 MB for videos)</p>
        </div>
        <FileUpload
          context="misc"
          label="Upload File"
          maxSizeMB={100}
          onUploaded={() => load()}
          dataTestid="upload-generic-btn"
        />
      </div>

      <FileUpload
        variant="dropzone"
        context="misc"
        multiple
        maxSizeMB={100}
        onUploaded={() => load()}
        label="Drop files here to upload"
        dataTestid="upload-dropzone"
      />

      {/* Context filter chips */}
      {contextsInUse.length > 1 && (
        <div className="flex items-center flex-wrap gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <button
            type="button"
            onClick={() => setFilter('')}
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${filter === '' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
            data-testid="filter-all"
          >All ({files.length})</button>
          {contextsInUse.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${filter === c ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
              data-testid={`filter-${c}`}
            >
              {CONTEXT_LABELS[c] || c} ({files.filter(f => f.context === c).length})
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">
          {filter ? `No files in ${CONTEXT_LABELS[filter] || filter}` : 'No files uploaded yet — drop something in!'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="files-grid">
          {visible.map(f => (
            <div key={f.id} className="nb-card p-3" data-testid={`file-card-${f.id}`}>
              <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold">
                  {CONTEXT_LABELS[f.context] || f.context}
                </span>
                <span className="text-[10px] text-slate-400">{formatBytes(f.size)}</span>
              </div>
              <FileChip file={f} onDelete={deleteFile} dataTestid={`chip`} />
              <p className="text-[10px] text-slate-400 mt-2">{(f.created_at || '').slice(0, 16).replace('T', ' ')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
